import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Check, User, ClipboardList,
  CalendarDays, Repeat, DollarSign, FileText, StickyNote, Save, Plus,
  CheckCircle2, PlusCircle, Eye,
} from "lucide-react";
import { api, formatZAR, todayISO, formatDate } from "../api";
import { toast } from "sonner";

const STEPS = [
  { key: "client", label: "Client", icon: User },
  { key: "task", label: "Task", icon: ClipboardList },
  { key: "date", label: "Date", icon: CalendarDays },
  { key: "repeat", label: "Repeat", icon: Repeat },
  { key: "price", label: "Price", icon: DollarSign },
  { key: "invoice", label: "Invoice", icon: FileText },
  { key: "notes", label: "Notes", icon: StickyNote },
  { key: "save", label: "Save", icon: Save },
];

export default function TaskWizard() {
  const [searchParams] = useSearchParams();
  const presetClientId = searchParams.get("client_id") || "";
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    client_id: "", client_name: "",
    task_category_id: "", task_name: "",
    date: todayISO(),
    repeat_type: "monthly",
    price: 0,
    suggested_price: 0,
    price_source: "standard",
    invoice_ref: "",
    notes: "",
  });
  const [savedTask, setSavedTask] = useState(null);
  const navigate = useNavigate();
  const [newClientName, setNewClientName] = useState("");
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskPrice, setNewTaskPrice] = useState(0);
  const [newTaskRepeat, setNewTaskRepeat] = useState("monthly");
  const [clientSearch, setClientSearch] = useState("");
  const [bizFilter, setBizFilter] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/task-categories")]).then(([cr, tr]) => {
      setClients(cr.data);
      setCategories(tr.data);
      // If we have a preset client, jump to step 2 (task)
      if (presetClientId) {
        const c = cr.data.find(x => x.id === presetClientId);
        if (c) {
          setForm(f => ({ ...f, client_id: c.id, client_name: c.name }));
          setStep(1);
        }
      }
    });
  }, [presetClientId]);

  // When client + task selected, fetch suggested price
  useEffect(() => {
    if (form.client_id && form.task_category_id) {
      api.get("/pricing/suggested", { params: { client_id: form.client_id, task_category_id: form.task_category_id } })
        .then(r => {
          setForm(f => ({
            ...f,
            suggested_price: r.data.price,
            price: r.data.price,
            price_source: r.data.source,
            repeat_type: r.data.default_repeat || f.repeat_type,
          }));
        });
    }
  }, [form.client_id, form.task_category_id]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase().trim();
    let list = clients;
    if (bizFilter) list = list.filter(c => (c.business || "A van Greunen") === bizFilter);
    if (q) list = list.filter(c => c.name.toLowerCase().includes(q));
    return list;
  }, [clients, clientSearch, bizFilter]);

  const canNext = () => {
    switch (step) {
      case 0: return !!form.client_id;
      case 1: return !!form.task_category_id;
      case 2: return !!form.date;
      case 3: return !!form.repeat_type;
      case 4: return form.price !== "" && !isNaN(Number(form.price));
      default: return true;
    }
  };

  const addClient = async () => {
    if (!newClientName.trim()) return;
    const r = await api.post("/clients", { name: newClientName.trim() });
    const created = r.data;
    setClients(prev => prev.some(c => c.id === created.id) ? prev : [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    setForm(f => ({ ...f, client_id: created.id, client_name: created.name }));
    setNewClientName("");
    setShowAddClient(false);
    toast.success("Client added");
  };

  const addCategory = async () => {
    if (!newTaskName.trim()) return;
    const r = await api.post("/task-categories", {
      name: newTaskName.trim(),
      default_price: Number(newTaskPrice) || 0,
      default_repeat: newTaskRepeat,
    });
    const created = r.data;
    setCategories(prev => prev.some(c => c.id === created.id) ? prev : [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    setForm(f => ({ ...f, task_category_id: created.id, task_name: created.name }));
    setNewTaskName(""); setNewTaskPrice(0); setNewTaskRepeat("monthly");
    setShowAddTask(false);
    toast.success("Task category added");
  };

  const save = async () => {
    try {
      const r = await api.post("/tasks", {
        client_id: form.client_id,
        task_category_id: form.task_category_id,
        date: form.date,
        repeat_type: form.repeat_type,
        price: Number(form.price),
        invoice_ref: form.invoice_ref || "",
        notes: form.notes || "",
      });
      toast.success("Task saved");
      setSavedTask({ ...r.data, client_name: form.client_name, task_name: form.task_name });
    } catch (e) {
      toast.error("Could not save task");
    }
  };

  const addAnotherForSameClient = () => {
    // Keep client; reset everything else and jump back to step 2 (task)
    setForm(f => ({
      client_id: f.client_id,
      client_name: f.client_name,
      task_category_id: "", task_name: "",
      date: todayISO(),
      repeat_type: "monthly",
      price: 0,
      suggested_price: 0,
      price_source: "standard",
      invoice_ref: "",
      notes: "",
    }));
    setSavedTask(null);
    setStep(1);
  };

  const addBrandNew = () => {
    setForm({
      client_id: "", client_name: "",
      task_category_id: "", task_name: "",
      date: todayISO(),
      repeat_type: "monthly",
      price: 0,
      suggested_price: 0,
      price_source: "standard",
      invoice_ref: "",
      notes: "",
    });
    setSavedTask(null);
    setStep(0);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="np-label mb-2">Enter work</div>
        <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
          Add a task in 8 quick steps
        </h1>
        <p className="text-[#595959] mt-2">Follow the guided steps. We'll suggest the right price based on history.</p>
      </div>

      {savedTask ? (
        <div className="np-card max-w-2xl mx-auto text-center" data-testid="wizard-saved-success">
          <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#065F46]" strokeWidth={2.5} />
          </div>
          <h2 className="text-2xl font-extrabold text-[#011617] mb-2" style={{ fontFamily: "Manrope" }}>
            Task saved
          </h2>
          <p className="text-[#595959] mb-1">
            <span className="font-semibold text-[#011617]">{savedTask.task_name}</span> for{" "}
            <span className="font-semibold text-[#011617]">{savedTask.client_name}</span>
          </p>
          <p className="text-sm text-[#595959] mb-6">
            {formatDate(savedTask.date)} • {formatZAR(savedTask.price)}
            {savedTask.invoice_ref ? ` • Inv ${savedTask.invoice_ref}` : " • Outstanding"}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="wizard-add-another-same-client"
              onClick={addAnotherForSameClient}
              className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center justify-center gap-2"
            >
              <PlusCircle className="w-4 h-4" /> Add another task for {savedTask.client_name}
            </button>
            <button
              data-testid="wizard-add-different-client"
              onClick={addBrandNew}
              className="bg-white border border-[#CCCCCC] hover:border-[#0F4F51] text-[#011617] font-semibold rounded-lg px-5 py-2.5 inline-flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Task for a different client
            </button>
            <button
              data-testid="wizard-view-client-month"
              onClick={() => navigate(`/client-month?client_id=${savedTask.client_id}`)}
              className="bg-[#E5FFFF] hover:bg-[#00DDE6] text-[#0F4F51] hover:text-[#011617] font-semibold rounded-lg px-5 py-2.5 inline-flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" /> View client month
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Stepper */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  data-testid={`step-${s.key}-btn`}
                  onClick={() => i < step && setStep(i)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                    active ? "bg-[#00ACB3] text-white" :
                    done ? "bg-[#0F4F51] text-white" :
                    "bg-white text-[#595959] border border-[#CCCCCC]"
                  }`}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                  {i + 1}. {s.label}
                </button>
                {i < STEPS.length - 1 && <div className="w-4 h-px bg-[#CCCCCC]" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main step content */}
        <div className="lg:col-span-2 np-card">
          {step === 0 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Select or add client</h2>
              <p className="text-sm text-[#595959] mb-4">Search to find an existing client. Or add a new one below.</p>

              {form.client_id ? (
                /* Selected client confirmation */
                <div className="rounded-lg border-2 border-[#00ACB3] bg-[#E5FFFF] p-4 flex items-center justify-between gap-3" data-testid="wizard-selected-client">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#00ACB3] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="np-label">Selected client</div>
                      <div className="text-lg font-extrabold text-[#011617] truncate" style={{ fontFamily: "Manrope" }}>
                        {form.client_name}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="wizard-change-client-btn"
                    onClick={() => setForm(f => ({ ...f, client_id: "", client_name: "" }))}
                    className="text-sm font-semibold text-[#0F4F51] hover:text-[#011617] underline shrink-0"
                  >Change</button>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      data-testid="wizard-biz-all"
                      onClick={() => setBizFilter("")}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        bizFilter === "" ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
                      }`}
                    >All</button>
                    <button
                      type="button"
                      data-testid="wizard-biz-avg"
                      onClick={() => setBizFilter("A van Greunen")}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        bizFilter === "A van Greunen" ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
                      }`}
                    >A van Greunen</button>
                    <button
                      type="button"
                      data-testid="wizard-biz-netmark"
                      onClick={() => setBizFilter("NetMark Pro")}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        bizFilter === "NetMark Pro" ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
                      }`}
                    >NetMark Pro</button>
                  </div>
                  <input
                    data-testid="wizard-client-search"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    className="w-full px-3 py-2.5 mb-3 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
                  />
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-[#CCCCCC] divide-y divide-[#CCCCCC]">
                    {filteredClients.map(c => (
                      <button
                        type="button"
                        key={c.id}
                        data-testid={`wizard-pick-client-${c.id}`}
                        onClick={() => setForm(f => ({ ...f, client_id: c.id, client_name: c.name }))}
                        className="w-full text-left px-3 py-2.5 hover:bg-[#E5FFFF] transition-colors flex justify-between items-center"
                      >
                        <span>{c.name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0F4F51]">{c.business || "A van Greunen"}</span>
                      </button>
                    ))}
                  </div>
                  {!showAddClient ? (
                    <button
                      type="button"
                      data-testid="wizard-show-add-client"
                      onClick={() => setShowAddClient(true)}
                      className="mt-4 text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Client not in list? Add a new one
                    </button>
                  ) : (
                    <div className="mt-5 p-4 rounded-lg bg-[#F4F4F4]">
                      <div className="np-label mb-2">Add new client</div>
                      <div className="flex gap-2">
                        <input
                          data-testid="wizard-new-client-input"
                          value={newClientName}
                          onChange={e => setNewClientName(e.target.value)}
                          placeholder="New client name"
                          className="flex-1 px-3 py-2 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] outline-none bg-white"
                        />
                        <button
                          data-testid="wizard-add-client-btn"
                          onClick={addClient}
                          className="bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1 hover:bg-[#011617]"
                        >
                          <Plus className="w-4 h-4" /> Add
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Select or add task</h2>
              <p className="text-sm text-[#595959] mb-4">Keep task names clean and reusable. Don't mix in client or domain names.</p>

              {form.task_category_id ? (
                <div className="rounded-lg border-2 border-[#00ACB3] bg-[#E5FFFF] p-4 flex items-center justify-between gap-3" data-testid="wizard-selected-task">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#00ACB3] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="np-label">Selected task</div>
                      <div className="text-lg font-extrabold text-[#011617] truncate" style={{ fontFamily: "Manrope" }}>
                        {form.task_name}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="wizard-change-task-btn"
                    onClick={() => setForm(f => ({ ...f, task_category_id: "", task_name: "" }))}
                    className="text-sm font-semibold text-[#0F4F51] hover:text-[#011617] underline shrink-0"
                  >Change</button>
                </div>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-[#CCCCCC] divide-y divide-[#CCCCCC]">
                    {categories.map(c => (
                      <button
                        type="button"
                        key={c.id}
                        data-testid={`wizard-pick-task-${c.id}`}
                        onClick={() => setForm(f => ({ ...f, task_category_id: c.id, task_name: c.name }))}
                        className="w-full text-left px-3 py-2.5 hover:bg-[#E5FFFF] transition-colors flex justify-between"
                      >
                        <span>{c.name}</span>
                        <span className="text-xs text-[#595959]">
                          {c.default_repeat} • {formatZAR(c.default_price)}
                        </span>
                      </button>
                    ))}
                  </div>
                  {!showAddTask ? (
                    <button
                      type="button"
                      data-testid="wizard-show-add-task"
                      onClick={() => setShowAddTask(true)}
                      className="mt-4 text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Task not in list? Add a new category
                    </button>
                  ) : (
                    <div className="mt-5 p-4 rounded-lg bg-[#F4F4F4]">
                      <div className="np-label mb-2">Add new task category</div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <input
                          data-testid="wizard-new-task-name"
                          value={newTaskName}
                          onChange={e => setNewTaskName(e.target.value)}
                          placeholder="Task name"
                          className="md:col-span-2 px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none bg-white"
                        />
                        <input
                          data-testid="wizard-new-task-price"
                          type="number"
                          step="0.01"
                          value={newTaskPrice}
                          onChange={e => setNewTaskPrice(e.target.value)}
                          placeholder="Std. price"
                          className="px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none bg-white"
                        />
                        <select
                          data-testid="wizard-new-task-repeat"
                          value={newTaskRepeat}
                          onChange={e => setNewTaskRepeat(e.target.value)}
                          className="px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none bg-white"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="annual">Annual</option>
                          <option value="once_off">Once-off</option>
                        </select>
                      </div>
                      <button
                        data-testid="wizard-add-task-btn"
                        onClick={addCategory}
                        className="mt-2 bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1 hover:bg-[#011617]"
                      >
                        <Plus className="w-4 h-4" /> Add task category
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Choose date</h2>
              <p className="text-sm text-[#595959] mb-4">When does this task apply?</p>
              <input
                data-testid="wizard-date-input"
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] outline-none bg-white"
              />
              <p className="text-xs text-[#595959] mt-2">Shown as {formatDate(form.date)}</p>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Repeat type</h2>
              <p className="text-sm text-[#595959] mb-4">Annual tasks generate a renewal reminder one month before due. "Custom" = irregular cadence you manage yourself.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["monthly", "annual", "once_off", "custom"].map(rt => (
                  <button
                    key={rt}
                    type="button"
                    data-testid={`wizard-repeat-${rt}`}
                    onClick={() => setForm(f => ({ ...f, repeat_type: rt }))}
                    className={`rounded-lg px-4 py-3 border font-semibold ${
                      form.repeat_type === rt ? "bg-[#00ACB3] text-white border-[#00ACB3]" : "bg-white text-[#011617] border-[#CCCCCC]"
                    }`}
                  >
                    {rt === "once_off" ? "Once-off" : rt.charAt(0).toUpperCase() + rt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Confirm price</h2>
              <p className="text-sm text-[#595959] mb-2">
                Suggested: <span className="font-bold text-[#0F4F51]">{formatZAR(form.suggested_price)}</span>
                {" "}({form.price_source === "client_specific" ? "remembered for this client" : "standard pricing index"})
              </p>
              <p className="text-xs text-[#595959] mb-4">Override below if needed - we'll remember your new price for this client going forward.</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-[#0F4F51]">R</span>
                <input
                  data-testid="wizard-price-input"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                  className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] outline-none bg-white w-48"
                />
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, price: f.suggested_price }))}
                  className="text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51]"
                >Use suggested</button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Invoice reference</h2>
              <p className="text-sm text-[#595959] mb-4">Leave blank to mark as outstanding. You can fill it in later.</p>
              <input
                data-testid="wizard-invoice-input"
                value={form.invoice_ref}
                onChange={e => setForm(f => ({ ...f, invoice_ref: e.target.value }))}
                placeholder="e.g. A003"
                className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] outline-none bg-white w-64"
              />
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Notes</h2>
              <p className="text-sm text-[#595959] mb-4">Add domain names or context here — keep them out of task names.</p>
              <textarea
                data-testid="wizard-notes-input"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
                placeholder="Optional notes..."
                className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] outline-none bg-white"
              />
            </div>
          )}

          {step === 7 && (
            <div>
              <h2 className="text-xl font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Review and save</h2>
              <p className="text-sm text-[#595959] mb-4">Confirm all the details on the right look correct, then save.</p>
              <button
                data-testid="wizard-save-btn"
                onClick={save}
                className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-6 py-3 inline-flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Save task
              </button>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-[#CCCCCC]">
            <button
              data-testid="wizard-prev-btn"
              type="button"
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-[#CCCCCC] font-semibold text-[#011617] hover:border-[#0F4F51] disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < STEPS.length - 1 && (
              <button
                data-testid="wizard-next-btn"
                type="button"
                onClick={() => canNext() && setStep(s => s + 1)}
                disabled={!canNext()}
                className="inline-flex items-center gap-1 px-5 py-2 rounded-lg bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold disabled:opacity-40"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Summary receipt */}
        <aside className="np-card h-fit sticky top-6">
          <h3 className="text-lg font-bold text-[#011617] mb-3" style={{ fontFamily: "Manrope" }}>Summary</h3>
          <dl className="space-y-3 text-sm">
            <Row label="Client" value={form.client_name || "—"} />
            <Row label="Task" value={form.task_name || "—"} />
            <Row label="Date" value={form.date ? formatDate(form.date) : "—"} />
            <Row label="Repeat" value={form.repeat_type === "once_off" ? "Once-off" : form.repeat_type ? form.repeat_type.charAt(0).toUpperCase() + form.repeat_type.slice(1) : "—"} />
            <Row label="Price" value={form.price ? formatZAR(form.price) : "—"} bold />
            <Row label="Invoice" value={form.invoice_ref || <span className="badge-outstanding">Outstanding</span>} />
            <Row label="Notes" value={form.notes || "—"} />
          </dl>
        </aside>
      </div>
      </>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="np-label shrink-0">{label}</dt>
      <dd className={`text-right text-[#011617] ${bold ? "font-extrabold" : "font-medium"}`}>{value}</dd>
    </div>
  );
}
